import { AlertEntity, DefaultAlertEntity, SaveAlertPayload } from '@src/entities/Alert/includes/types';
import { Table } from '@src/entities/Alert/ui/components/Modal/components/Table';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useForm, useFormReturn } from '@uhg-abyss/web/hooks/useForm';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import { isEmpty, isEqual, isUndefined, merge } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';

import fields from './includes/fields.json';

const { assignedUser, notes, status } = fields as Record<string, any>;

export type FormModalProps = {
  currentEntity?: AlertEntity;
  handleClose: () => void;
  handleSave: (payload: SaveAlertPayload) => Promise<void>;
};

/**
 * Form: Alert
 *
 * Renders a form for creating or editing an alert entity.
 * Handles form state, validation, and submission.
 */
export function Form({ currentEntity, handleClose, handleSave }: FormModalProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [initialValues, setInitialValues] = useState<AlertEntity>(DefaultAlertEntity);

  const form: useFormReturn<AlertEntity> = useForm({ defaultValues: DefaultAlertEntity });
  const { defaultValues, errors, isDirty, isSubmitting, isValid } = form.formState;
  const formValues = form.getValues();

  /**
   * Loads current entity data into the form if editing.
   */
  useEffect(() => {
    if (!isEmpty(currentEntity)) {
      const data: AlertEntity = merge({}, DefaultAlertEntity, {
        assignedUser: currentEntity?.assignedUser ?? '',
        id: currentEntity?.id ?? '',
        notes: currentEntity?.notes ?? '',
        status: currentEntity?.status ?? '',
        triggeredAlertId: currentEntity?.triggeredAlertId ?? '',
      });

      setInitialValues(data);

      form.reset(data, {
        keepDirty: false,
        keepDirtyValues: false,
        keepErrors: false,
        keepIsValid: false,
        keepSubmitCount: true,
        keepTouched: false,
        keepValues: false,
      });
    }
  }, [currentEntity]);

  /**
   * Handles form submission.
   * @param {SaveAlertPayload} submittedValues - The values to submit.
   */
  const handleSubmit = useCallback(
    async (submittedValues: SaveAlertPayload): Promise<void> => {
      if (!isLoading && isValid && !isSubmitting && isDirty) {
        setIsLoading(true);
        handleClose();
        await handleSave(submittedValues);
        setIsLoading(false);
      }
    },
    [isLoading, isDirty, isValid, isSubmitting, errors, currentEntity, handleClose, handleSave]
  );

  /**
   * Enables or disables the submit button based on form state.
   */
  useEffect(() => {
    if (!isEmpty(currentEntity)) {
      if (isEqual(formValues, initialValues) || !isValid) {
        setIsDisabled(true);
      } else {
        setIsDisabled(false);
      }
    } else if (!isValid) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [currentEntity, isDirty, isValid, formValues, defaultValues, initialValues]);

  return (
    <ErrorHandler location="src/entities/Alert/ui/components/Form/Form.tsx">
      <FormProvider autoComplete="off" onSubmit={handleSubmit} state={form}>
        <Grid>
          <Grid.Col
            span={{
              xs: '100%',
            }}
          >
            {!isUndefined(currentEntity) && !isEmpty(currentEntity) && <Table alert={currentEntity} />}
          </Grid.Col>
          <Grid.Col
            span={{
              xs: '50%',
            }}
          >
            <SelectInput {...status} />
          </Grid.Col>
          <Grid.Col
            span={{
              xs: '50%',
            }}
          >
            <TextInput {...assignedUser} />
          </Grid.Col>
          <Grid.Col
            span={{
              xs: '100%',
            }}
          >
            <TextInputArea {...notes} />
          </Grid.Col>
          <Grid.Col key="actions" span={{ xs: '100%' }}>
            <Divider height={1} />
            <Layout.Group alignLayout="right">
              <Button onClick={handleClose} variant="outline">
                Cancel
              </Button>
              <Button isDisabled={isDisabled} type="submit" variant="solid">
                {!isEmpty(currentEntity) ? 'Save' : 'Create'}
              </Button>
            </Layout.Group>
          </Grid.Col>
        </Grid>
      </FormProvider>
    </ErrorHandler>
  );
}
